export type HubspotApiMeta = {
  statusCode?: number
  statusText?: string
  method?: string
  url?: string
  message?: string
  category?: string
  correlationId?: string
  errors?: unknown
  responseBody?: unknown
  request?: unknown
}

export type HubspotEventContext = {
  companyId?: string | null
  companyName?: string | null
  domain?: string | null
  action?: string | null
}

export type HubspotEventDetails = {
  api: HubspotApiMeta | null
  operation?: string
  input: Record<string, unknown> | unknown[] | null
  messageContext?: Record<string, unknown> | null
  response: unknown
  hubspotRecordUrl?: string | null
  hubspotRecordId?: string | null
}

const TERMINAL_EXECUTE_EVENTS = new Set([
  'company_merged',
  'review_task_created',
  'company_domain_updated',
  'company_deleted',
  'contact_merged',
  'contact_deleted',
  'hubspot_completed',
])

export const hubspotRecordUrlFromResponse = (response: unknown): string | null => {
  if (response && typeof response === 'object' && 'url' in response) {
    const url = (response as { url?: unknown }).url
    return typeof url === 'string' ? url : null
  }
  return null
}

const hubspotResponseFromMetadata = (metadata: Record<string, unknown>): unknown => {
  if (metadata.hubspotResponse != null) {
    return metadata.hubspotResponse
  }

  const steps = metadata.steps
  if (Array.isArray(steps) && steps.length > 0) {
    const lastStep = steps[steps.length - 1]
    if (lastStep && typeof lastStep === 'object' && 'hubspotResponse' in lastStep) {
      return (lastStep as { hubspotResponse?: unknown }).hubspotResponse ?? null
    }
  }

  return null
}

export const actionFromExecuteEvent = (event: string): string | null => {
  switch (event) {
    case 'company_merged':
      return 'MERGE'
    case 'review_task_created':
      return 'FLAG_FOR_REVIEW'
    case 'company_domain_updated':
      return 'UPDATE_URL'
    case 'company_deleted':
      return 'DELETE'
    case 'contact_deleted':
      return 'DELETE'
    case 'contact_merged':
      return 'MERGE'
    default:
      return null
  }
}

export const parseHubspotEventMetadata = (
  metadata: Record<string, unknown> | null | undefined,
  context?: HubspotEventContext
): HubspotEventDetails => {
  const api =
    metadata?.hubspotApi && typeof metadata.hubspotApi === 'object'
      ? (metadata.hubspotApi as HubspotApiMeta)
      : null

  const contextInput: Record<string, unknown> = {}
  if (context?.companyId) contextInput.companyId = context.companyId
  if (context?.companyName) contextInput.companyName = context.companyName
  if (context?.domain) contextInput.domain = context.domain
  if (context?.action) contextInput.action = context.action

  for (const key of [
    'targetCompanyId',
    'companyIdToMerge',
    'newDomain',
    'hubspotTaskId',
    'hubspotOperation',
  ]) {
    if (metadata?.[key] != null) {
      contextInput[key] = metadata[key]
    }
  }

  const requestFromApi = api?.request
  const requestSummary = metadata?.requestSummary ?? metadata?.input

  let input: HubspotEventDetails['input'] = null
  if (requestFromApi != null) {
    input = requestFromApi as Record<string, unknown> | unknown[]
  } else if (requestSummary != null) {
    input = requestSummary as Record<string, unknown> | unknown[]
  } else if (Object.keys(contextInput).length > 0) {
    input = contextInput
  }

  const messageContext =
    metadata?.messageContext && typeof metadata.messageContext === 'object'
      ? (metadata.messageContext as Record<string, unknown>)
      : null

  const response =
    metadata?.hubspotResponse ??
    (metadata ? hubspotResponseFromMetadata(metadata) : null) ??
    api?.responseBody ??
    api?.errors ??
    null

  const hubspotRecordUrl =
    typeof metadata?.hubspotRecordUrl === 'string'
      ? metadata.hubspotRecordUrl
      : hubspotRecordUrlFromResponse(response)

  return {
    api,
    operation:
      typeof metadata?.hubspotOperation === 'string' ? metadata.hubspotOperation : undefined,
    input,
    messageContext,
    response,
    hubspotRecordUrl,
    hubspotRecordId:
      typeof metadata?.hubspotRecordId === 'string'
        ? metadata.hubspotRecordId
        : typeof metadata?.hubspotTaskId === 'string'
          ? metadata.hubspotTaskId
          : typeof metadata?.finalCompanyId === 'string'
            ? metadata.finalCompanyId
            : typeof metadata?.mergedCompanyId === 'string'
              ? metadata.mergedCompanyId
              : null,
  }
}

type StepWithMetadata = {
  phase: string
  event: string
  action?: string | null
  companyId?: string | null
  companyName?: string | null
  domain?: string | null
  metadata: Record<string, unknown> | null
}

export const findTerminalExecutionStep = <T extends StepWithMetadata>(steps: T[]): T | null => {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index]
    if (step.phase === 'execute' && TERMINAL_EXECUTE_EVENTS.has(step.event)) {
      return step
    }
  }
  return null
}

export const extractTerminalExecutionResult = <T extends StepWithMetadata>(
  steps: T[],
  terminal?: string | null
) => {
  const step = findTerminalExecutionStep(steps)
  if (!step) {
    return null
  }

  const action = step.action ?? actionFromExecuteEvent(step.event) ?? terminal ?? null

  return {
    step,
    action,
    details: parseHubspotEventMetadata(step.metadata, {
      companyId: step.companyId,
      companyName: step.companyName,
      domain: step.domain,
      action,
    }),
  }
}

export const isHubspotFailureEvent = (
  event: string,
  metadata: Record<string, unknown> | null | undefined
): boolean => {
  if (event === 'hubspot_failed') {
    return true
  }

  return (
    event === 'group_completed' &&
    metadata?.status === 'failed' &&
    metadata?.reason === 'hubspot_failed' &&
    metadata?.hubspotApi != null
  )
}

export const formatJson = (value: unknown) => {
  if (value == null) return null
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
